import React, { useCallback, useEffect, useState, useMemo } from "react";
import PropTypes from "prop-types";
import Select from "react-select";
import { PageWrapper } from "../../../shared/layout";
import { ButtonToolbar, Button } from "react-bootstrap";
import { RegionSelector } from "../../../shared/components";
import { fetchVehiclesList } from "../reports/services/reportService";
import "./delayAnalysis.css";
import { DatePickerComponent } from "../../../shared/widgets";
import TimeRangePicker from "@wojtekmaj/react-timerange-picker/dist/TimeRangePicker";
import {
    GoogleMap,
    useJsApiLoader,
    Marker,
    Polyline,
    InfoWindow,
    OverlayView,
} from "@react-google-maps/api";
import { googleProperties } from "../../../services/login/properties";
import { fetchDelayAnalysis } from "../../../services/delayAnalysisService";
import {
    STATUS_COLORS,
    MAP_STYLE,
    DEFAULT_CENTER,
    getYesterdayDate,
    getLastHourRange,
    buildTimestamps,
    formatTimestamp,
} from "./delayAnalysisUtils";
import ChaloLoader from "../../../shared/widgets/chaloLoader";
import { toast } from "react-toastify";

const RoutePolyline = React.memo(function RoutePolyline({ routePath }) {
    if (!routePath || routePath.length === 0) return null;
    return (
        <Polyline
            path={routePath}
            options={{
                strokeColor: "#000000",
                strokeOpacity: 0.8,
                strokeWeight: 4,
            }}
        />
    );
});

RoutePolyline.propTypes = {
    routePath: PropTypes.arrayOf(
        PropTypes.shape({
            lat: PropTypes.number,
            lng: PropTypes.number,
        })
    ),
};

const Markers = React.memo(function Markers({
    packets,
    onMarkerClick,
    visibleStatuses,
}) {
    if (!packets || packets.length < 3) return null;

    return packets.slice(1, -1).map((packet) => {
        if (!visibleStatuses[packet.vehicleHealthStatus]) {
            return null;
        }

        const icon = {
            path: window.google?.maps?.SymbolPath?.CIRCLE,
            fillColor:
                STATUS_COLORS[packet.vehicleHealthStatus] ||
                STATUS_COLORS.GREEN,
            fillOpacity: 0.9,
            strokeColor: "#fff",
            strokeWeight: 1,
            scale: 8,
        };

        return (
            <Marker
                key={packet.packetArrivedAt}
                position={{ lat: packet.latitude, lng: packet.longitude }}
                icon={icon}
                onClick={() => onMarkerClick(packet)}
            />
        );
    });
});

Markers.propTypes = {
    packets: PropTypes.arrayOf(PropTypes.object),
    onMarkerClick: PropTypes.func,
    visibleStatuses: PropTypes.object,
};

const DelayMap = React.memo(function DelayMap({
    packets,
    routePath,
    mapCenter,
    selectedMarker,
    onMarkerClick,
    onCloseInfo,
    visibleStatuses,
}) {
    const [mapLoaded, setMapLoaded] = useState(false);

    const handleMapLoad = useCallback(() => {
        setMapLoaded(true);
    }, []);

    return (
        <GoogleMap
            mapContainerStyle={MAP_STYLE}
            center={mapCenter}
            zoom={13}
            onLoad={handleMapLoad}
        >
            <RoutePolyline routePath={routePath} />

            <Markers
                packets={packets}
                onMarkerClick={onMarkerClick}
                visibleStatuses={visibleStatuses}
            />

            {mapLoaded && packets.length > 0 && (
                <OverlayView
                    position={{
                        lat: packets[0].latitude,
                        lng: packets[0].longitude,
                    }}
                    mapPaneName={OverlayView.OVERLAY_MOUSE_TARGET}
                >
                    <button
                        className="delay-analysis-marker"
                        onClick={() => onMarkerClick(packets[0])}
                    >
                        End
                    </button>
                </OverlayView>
            )}

            {mapLoaded && packets.length > 1 && (
                <OverlayView
                    position={{
                        lat: packets[packets.length - 1].latitude,
                        lng: packets[packets.length - 1].longitude,
                    }}
                    mapPaneName={OverlayView.OVERLAY_MOUSE_TARGET}
                >
                    <button
                        className="delay-analysis-marker"
                        onClick={() =>
                            onMarkerClick(packets[packets.length - 1])
                        }
                    >
                        Start
                    </button>
                </OverlayView>
            )}

            {selectedMarker && (
                <InfoWindow
                    position={{
                        lat: selectedMarker.latitude,
                        lng: selectedMarker.longitude,
                    }}
                    onCloseClick={onCloseInfo}
                >
                    <div className="delay-analysis-info-window">
                        <h4>Vehicle Details</h4>

                        <div className="delay-analysis-info-content">
                            <div className="delay-analysis-info-row">
                                <strong>Vehicle No:</strong>
                                <span>{selectedMarker.vehicleNo}</span>
                            </div>

                            <div className="delay-analysis-info-row">
                                <strong>Device ID:</strong>
                                <span>{selectedMarker.deviceId}</span>
                            </div>
                            <div className="delay-analysis-info-row">
                                <strong>Ignition Status:</strong>
                                <span>
                                    {selectedMarker.ignitionStatus === 1
                                        ? "ON"
                                        : "OFF"}
                                </span>
                            </div>
                            <div className="delay-analysis-info-row">
                                <strong>Status:</strong>
                                <span
                                    className="delay-analysis-status-badge"
                                    style={{
                                        backgroundColor:
                                            STATUS_COLORS[
                                                selectedMarker
                                                    .vehicleHealthStatus
                                            ] || STATUS_COLORS.DEFAULT,
                                    }}
                                >
                                    {selectedMarker.vehicleHealthStatus}
                                </span>
                            </div>

                            <div className="delay-analysis-info-row">
                                <strong>Coordinates:</strong>
                                <span>
                                    {typeof selectedMarker.latitude === "number" ? selectedMarker.latitude.toFixed(6) : "N/A"},{" "}
                                    {typeof selectedMarker.longitude === "number" ? selectedMarker.longitude.toFixed(6) : "N/A"}
                                </span>
                            </div>

                            <div className="delay-analysis-info-row">
                                <strong>Timestamp:</strong>
                                <span className="delay-analysis-timestamp">
                                    {selectedMarker.timestamp ? formatTimestamp(selectedMarker.timestamp) : "N/A"}
                                </span>
                            </div>

                            <div className="delay-analysis-info-row">
                                <strong>Packet Arrived:</strong>
                                <span className="delay-analysis-timestamp">
                                    {selectedMarker.packetArrivedAt ? formatTimestamp(selectedMarker.packetArrivedAt) : "N/A"}
                                </span>
                            </div>
                        </div>
                    </div>
                </InfoWindow>
            )}
        </GoogleMap>
    );
});

DelayMap.propTypes = {
    packets: PropTypes.arrayOf(PropTypes.object),
    routePath: PropTypes.arrayOf(
        PropTypes.shape({
            lat: PropTypes.number,
            lng: PropTypes.number,
        })
    ),
    mapCenter: PropTypes.shape({
        lat: PropTypes.number,
        lng: PropTypes.number,
    }),
    selectedMarker: PropTypes.object,
    onMarkerClick: PropTypes.func,
    onCloseInfo: PropTypes.func,
    visibleStatuses: PropTypes.object,
};

function DelayAnalysis() {
    const [regionId, setRegionId] = useState(null);
    const [vehicles, setVehicles] = useState([]);
    const [selectedVehicle, setSelectedVehicle] = useState(null);
    const [selectedDate, setSelectedDate] = useState(getYesterdayDate);
    const [timeSelected, setTimeSelected] = useState(getLastHourRange);

    const [delayData, setDelayData] = useState(null);
    const [selectedMarker, setSelectedMarker] = useState(null);

    const [loadingVehicles, setLoadingVehicles] = useState(false);
    const [loadingDelay, setLoadingDelay] = useState(false);

    const [visibleStatuses, setVisibleStatuses] = useState({
        GREEN: true,
        YELLOW: true,
        RED: true,
    });

    const showToast = (type, message) => {
        const styles = {
            error: { backgroundColor: "rgb(228, 190, 190)", color: "#222" },
            success: { backgroundColor: "green", color: "#fff" },
        };

        toast[type](message, { style: styles[type] });
    };
    const { isLoaded } = useJsApiLoader({
        googleMapsApiKey: googleProperties.map.api_key,
    });

    const packets = useMemo(
        () => delayData?.vehicleTelemetryHealthList || [],
        [delayData],
    );

    const routePath = useMemo(() => {
        if (!packets || packets.length === 0) return [];
        return packets.map((p) => {
            if (!p || typeof p.latitude === "undefined" || typeof p.longitude === "undefined") {
                return null;
            }
            return { lat: p.latitude, lng: p.longitude };
        }).filter(Boolean);
    }, [packets]);

    const mapCenter = useMemo(() => {
        if (!packets || packets.length === 0) return DEFAULT_CENTER;
        const firstPacket = packets[0];
        if (!firstPacket || typeof firstPacket.latitude === "undefined" || typeof firstPacket.longitude === "undefined") {
            return DEFAULT_CENTER;
        }
        return { lat: firstPacket.latitude, lng: firstPacket.longitude };
    }, [packets]);

    const stats = useMemo(() => {
        if (!delayData) return null;
        const total = packets.length || 0;
        return {
            green: Math.max(0, delayData.totalGreen || 0),
            yellow: Math.max(0, delayData.totalYellow || 0),
            red: Math.max(0, delayData.totalRed || 0),
            total: total,
        };
    }, [delayData, packets]);

    useEffect(() => {
        if (!regionId) {
            setVehicles([]);
            setSelectedVehicle(null);
            return;
        }

        const controller = new AbortController();
        setLoadingVehicles(true);

        fetchVehiclesList(regionId, { signal: controller.signal })
            .then((response) => {
                if (Array.isArray(response)) {
                    setVehicles(
                        response.map((v) => ({ label: v || "", value: v })),
                    );
                } else {
                    setVehicles([]);
                }
            })
            .catch((err) => {
                if (err?.name !== "AbortError") {
                    showToast("error", "Failed to load vehicles");
                    setVehicles([]);
                }
            })
            .finally(() => setLoadingVehicles(false));

        return () => controller.abort();
    }, [regionId]);

    const validateTimeRange = useCallback((timeRange) => {
        if (!timeRange || timeRange.length !== 2) return false;

        const [startTime, endTime] = timeRange;
        if (!startTime || !endTime) return false;

        const start = new Date(`1970-01-01 ${startTime}`);
        const end = new Date(`1970-01-01 ${endTime}`);

        const diffMs = end - start;
        const diffHours = diffMs / (1000 * 60 * 60);

        if (diffHours > 3) {
            showToast(
                "error",
                "Time range cannot exceed 3 hours. Please select a shorter time range.",
            );
            return false;
        }

        return true;
    }, []);

    const fetchData = useCallback(() => {
        if (!regionId || !selectedVehicle || !selectedDate || !timeSelected) {
            return;
        }

        if (!validateTimeRange(timeSelected)) {
            return;
        }

        const timestamps = buildTimestamps(selectedDate, timeSelected);
        if (!timestamps) {
            console.error("Invalid date/time range");
            return;
        }

        setLoadingDelay(true);
        setSelectedMarker(null);

        fetchDelayAnalysis({
            vehicleNo: selectedVehicle?.value,
            ...timestamps,
            regionId,
            timeZone: "Asia/Kolkata",
        })
            .then((data) => {
                if (data) {
                    setDelayData(data);
                    showToast("success","Delay analysis data loaded");
                } else {
                    setDelayData(null);
                    showToast("error", "No data found for selected filters");
                }
            })
            .catch((err) => {
                showToast("error", "Failed to fetch delay analysis");
                setDelayData(null);
            })
            .finally(() => {
                setLoadingDelay(false);
            });
    }, [
        regionId,
        selectedVehicle,
        selectedDate,
        timeSelected,
        validateTimeRange,
    ]);

    const handleVehicleChange = useCallback(
        (vehicle) => {
            setSelectedVehicle(vehicle);
            setDelayData(null);
            setSelectedMarker(null);

            if (vehicle && regionId && selectedDate && timeSelected) {
                setTimeout(() => {
                    if (validateTimeRange(timeSelected)) {
                        const timestamps = buildTimestamps(
                            selectedDate,
                            timeSelected,
                        );
                        if (timestamps) {
                            setLoadingDelay(true);
                            fetchDelayAnalysis({
                                vehicleNo: vehicle.value,
                                ...timestamps,
                                regionId,
                                timeZone: "Asia/Kolkata",
                            })
                                .then(setDelayData)
                                .catch((err) => {
                                    showToast("error", "Failed to fetch delay analysis");
                                    setDelayData(null);
                                })
                                .finally(() => setLoadingDelay(false));
                        }
                    }
                }, 0);
            }
        },
        [regionId, selectedDate, timeSelected, validateTimeRange],
    );

    const handleRegionChange = useCallback((id) => {
        setRegionId(id);
        setSelectedVehicle(null);
        setDelayData(null);
        setSelectedMarker(null);
    }, []);

    const handleSubmit = useCallback(() => {
        fetchData();
    }, [fetchData]);

    const handleMarkerClick = useCallback((packet) => {
        setSelectedMarker(packet);
    }, []);

    const handleCloseInfo = useCallback(() => {
        setSelectedMarker(null);
    }, []);

    const handleStatusToggle = useCallback((status) => {
        setVisibleStatuses((prev) => ({
            ...prev,
            [status]: !prev[status],
        }));
    }, []);

    const isSubmitDisabled =
        !regionId || !selectedVehicle || !selectedDate || !timeSelected;

    return (
        <div className="wrapper">
            <PageWrapper.Header heading="GPS Delay Analysis">
                <div className="pull-right">
                    <ButtonToolbar className="d-flex gap-2">
                        <DatePickerComponent
                            date={selectedDate}
                            onDateChange={setSelectedDate}
                            todaySelectable
                        />
                        <TimeRangePicker
                            format="hh:mm a"
                            disableClock
                            clearIcon={null}
                            onChange={setTimeSelected}
                            value={timeSelected}
                        />
                        <Button
                            bsPrefix="ms-1 confirm-btn"
                            onClick={handleSubmit}
                            disabled={isSubmitDisabled}
                        >
                            Submit
                        </Button>
                    </ButtonToolbar>
                </div>
            </PageWrapper.Header>

            {loadingDelay && <ChaloLoader />}

            <PageWrapper.Body>
                <PageWrapper.BodyHeader>
                    <div className="header-button-container">
                        <div className="form-group">
                            <p className="filters-label">Filter By:</p>
                            <div className="dataTables_length wrapper-gps">
                                <ButtonToolbar>
                                    <RegionSelector
                                        onRegionChange={handleRegionChange}
                                    />
                                    <Select
                                        options={vehicles}
                                        value={selectedVehicle}
                                        onChange={handleVehicleChange}
                                        isDisabled={!regionId}
                                        isLoading={loadingVehicles}
                                        placeholder={
                                            regionId
                                                ? "Select Vehicle"
                                                : "Select Region first"
                                        }
                                        isClearable
                                        className="vehicle-selector"
                                    />
                                </ButtonToolbar>
                            </div>
                        </div>
                    </div>
                </PageWrapper.BodyHeader>

                {isLoaded ? (
                    <DelayMap
                        packets={packets}
                        routePath={routePath}
                        mapCenter={mapCenter}
                        selectedMarker={selectedMarker}
                        onMarkerClick={handleMarkerClick}
                        onCloseInfo={handleCloseInfo}
                        visibleStatuses={visibleStatuses}
                    />
                ) : (
                    <div className="delay-analysis-map-loader">
                        <div className="delay-analysis-map-loader-content">
                            <output
                                className="spinner-border text-primary delay-analysis-map-loader-spinner"
                                aria-label="Loading map"
                            />
                            <div>Loading map...</div>
                        </div>
                    </div>
                )}

                {stats && (
                    <div className="delay-analysis-stats-container">
                        <h5 className="delay-analysis-stats-title">
                            Packet Statistics
                        </h5>
                        <table className="delay-analysis-stats-table">
                            <thead>
                                <tr>
                                    <th className="delay-analysis-stats-table-header-cell">
                                        Show
                                    </th>
                                    <th style={{ textAlign: "center" }}>
                                        Status
                                    </th>
                                    <th className="delay-analysis-stats-table-header-count">
                                        Count
                                    </th>
                                    <th className="delay-analysis-stats-table-header-percentage">
                                        Percentage
                                    </th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr>
                                    <td className="delay-analysis-stats-table-data-checkbox">
                                        <input
                                            type="checkbox"
                                            checked={visibleStatuses.GREEN}
                                            onChange={() =>
                                                handleStatusToggle("GREEN")
                                            }
                                        />
                                    </td>
                                    <td className="delay-analysis-stats-table-data-status">
                                        <span
                                            className="delay-analysis-status-indicator"
                                            style={{
                                                backgroundColor:
                                                    STATUS_COLORS.GREEN,
                                            }}
                                        ></span>
                                        {"delay <= 20sec"}
                                    </td>
                                    <td className="delay-analysis-stats-table-data-count">
                                        {stats.green}
                                    </td>
                                    <td className="delay-analysis-stats-table-data-percentage">
                                        {stats.total
                                            ? (
                                                  (stats.green / stats.total) *
                                                  100
                                              ).toFixed(1)
                                            : "0.0"}
                                        %
                                    </td>
                                </tr>
                                <tr>
                                    <td className="delay-analysis-stats-table-data-checkbox">
                                        <input
                                            type="checkbox"
                                            checked={visibleStatuses.YELLOW}
                                            onChange={() =>
                                                handleStatusToggle("YELLOW")
                                            }
                                        />
                                    </td>
                                    <td className="delay-analysis-stats-table-data-status">
                                        <span
                                            className="delay-analysis-status-indicator"
                                            style={{
                                                backgroundColor:
                                                    STATUS_COLORS.YELLOW,
                                            }}
                                        ></span>
                                        {"20sec < delay < 2min"}
                                    </td>
                                    <td className="delay-analysis-stats-table-data-count">
                                        {stats.yellow}
                                    </td>
                                    <td className="delay-analysis-stats-table-data-percentage">
                                        {stats.total
                                            ? (
                                                  (stats.yellow / stats.total) *
                                                  100
                                              ).toFixed(1)
                                            : "0.0"}
                                        %
                                    </td>
                                </tr>
                                <tr>
                                    <td className="delay-analysis-stats-table-data-checkbox">
                                        <input
                                            type="checkbox"
                                            checked={visibleStatuses.RED}
                                            onChange={() =>
                                                handleStatusToggle("RED")
                                            }
                                        />
                                    </td>
                                    <td className="delay-analysis-stats-table-data-status">
                                        <span
                                            className="delay-analysis-status-indicator"
                                            style={{
                                                backgroundColor:
                                                    STATUS_COLORS.RED,
                                            }}
                                        ></span>
                                        {"delay >= 2min"}
                                    </td>
                                    <td className="delay-analysis-stats-table-data-count">
                                        {stats.red}
                                    </td>
                                    <td className="delay-analysis-stats-table-data-percentage">
                                        {stats.total
                                            ? (
                                                  (stats.red / stats.total) *
                                                  100
                                              ).toFixed(1)
                                            : "0.0"}
                                        %
                                    </td>
                                </tr>
                                <tr className="delay-analysis-stats-table-footer">
                                    <td></td>
                                    <td style={{ textAlign: "center" }}>
                                        Total Packets
                                    </td>
                                    <td className="delay-analysis-stats-table-data-count">
                                        {stats.total}
                                    </td>
                                    <td className="delay-analysis-stats-table-data-percentage">
                                        100%
                                    </td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                )}
            </PageWrapper.Body>
        </div>
    );
}

DelayAnalysis.propTypes = {};

export default DelayAnalysis;
